/**
 * Sentry's default PII switch does not protect against arbitrary payloads in
 * exception messages, request URLs, breadcrumbs, tags, or stack-frame vars.
 * Build the outgoing error event from a small allow-list instead of trying to
 * guess every possible name for a credential.
 */
export function sanitizeTelemetryEvent<T extends object>(event: T): T {
  const source = event as Record<string, unknown>;
  const exception = source.exception as { values?: Array<Record<string, unknown>> } | undefined;
  const values = exception?.values?.map((value) => {
    const stacktrace = value.stacktrace as { frames?: Array<Record<string, unknown>> } | undefined;
    const safeType = typeof value.type === "string" && /^[A-Za-z_$][\w.$]{0,79}$/.test(value.type)
      ? value.type : "Error";
    return {
      type: safeType,
      value: "An error occurred (details withheld)",
      ...(stacktrace?.frames && {
        stacktrace: {
          frames: stacktrace.frames.map((frame) => ({
            // No filenames, source context, locals or function strings: these
            // can include user-controlled paths and embedded credentials.
            ...(typeof frame.lineno === "number" && { lineno: frame.lineno }),
            ...(typeof frame.colno === "number" && { colno: frame.colno }),
          })),
        },
      }),
    };
  });
  return {
    ...(typeof source.event_id === "string" && { event_id: source.event_id }),
    ...(typeof source.timestamp === "number" && { timestamp: source.timestamp }),
    ...(typeof source.platform === "string" && { platform: source.platform }),
    ...(typeof source.level === "string" && { level: source.level }),
    ...(typeof source.message === "string" && { message: "An error occurred (details withheld)" }),
    ...(values && { exception: { values } }),
    // Never carry request/response bodies, URLs, cookies, headers, query
    // strings, arbitrary scope context, user data, or console breadcrumbs.
  } as T;
}

export function suppressTelemetryBreadcrumb(): null {
  // Even non-network breadcrumbs can contain a request body via console.log.
  return null;
}